import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MapPin,
  Phone,
  Truck,
} from "lucide-react";
import { FaWhatsapp as WhatsApp } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import { Footer, Header } from "./Home";
import ArticleSeo from "./articles/ArticleSeo";
import { SITE_URL, WHATSAPP_NUMBER } from "./homeTestsData";
import { GuestAccountMenu } from "./HomeTests";

const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000"
).replace(/\/$/, "");

const CLIENT_ID_KEY = "zaviramedHomeTestClientId";
const CLIENT_NAME_KEY = "zaviramedHomeTestClientName";
const CLIENT_TOKEN_KEY = "zaviramedHomeTestClientToken";

const steps = [
  { id: "pending", label: "Order made" },
  { id: "paid_product", label: "Kit paid" },
  { id: "processing", label: "Processing" },
  { id: "dispatch", label: "Dispatched" },
  { id: "delivered", label: "Delivered" },
];

function TrackingInfoCard({
  icon: Icon,
  title,
  artSrc,
  artAlt,
  artClassName = "",
  children,
}) {
  return (
    <div className="relative min-h-[150px] overflow-hidden rounded-2xl border border-emerald-900/10 bg-white p-5 shadow-[0_14px_42px_rgba(7,94,84,0.06)]">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white via-white to-emerald-50/70" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white" />
      <img
        src={artSrc}
        alt={artAlt}
        aria-hidden="true"
        className={`pointer-events-none  absolute bottom-[-26px] right-[-18px] max-w-none object-contain opacity-[0.35] mix-blend-multiply  ${artClassName}`}
        loading="lazy"
      />

      <div className="relative z-10 max-w-[68%] sm:max-w-[58%]">
        <div className="flex items-center gap-2">
          <Icon className="text-[#075E54]" size={18} strokeWidth={2.5} />
          <h2 className="text-sm font-black tracking-[-0.02em] text-slate-950">
            {title}
          </h2>
        </div>
        <div className="mt-4 space-y-2 text-sm font-semibold leading-6 text-slate-600">
          {children}
        </div>
      </div>
    </div>
  );
}

function saveAccount(client) {
  if (client?._id) localStorage.setItem(CLIENT_ID_KEY, client._id);
  if (client?.name) localStorage.setItem(CLIENT_NAME_KEY, client.name);
  window.dispatchEvent(new Event("zaviramed-home-test-account"));
}

function getClientToken() {
  if (typeof window === "undefined") return "";

  return localStorage.getItem(CLIENT_TOKEN_KEY) || "";
}

function Stepper({ status }) {
  const activeIndex =
    status === "failed"
      ? 0
      : Math.max(
          steps.findIndex((step) => step.id === status),
          0
        );

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {steps.map((step, index) => {
        const active = index <= activeIndex && status !== "failed";
        const current = step.id === status;

        return (
          <div
            key={step.id}
            aria-current={current ? "step" : undefined}
            className={`rounded-2xl border p-4 ${
              active
                ? "border-emerald-100 bg-emerald-50"
                : "border-slate-200 bg-white"
            }`}
          >
            <div
              className={`grid h-8 w-8 place-items-center rounded-full ${
                active ? "bg-[#25D366] text-[#075E54]" : "bg-slate-100 text-slate-400"
              }`}
            >
              <CheckCircle2 size={17} />
            </div>
            <p
              className={`mt-3 text-xs font-black ${
                current ? "text-[#075E54]" : "text-slate-600"
              }`}
            >
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default function HomeTestTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientToken, setClientToken] = useState(() => getClientToken());

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [orderId]);

  useEffect(() => {
    let alive = true;

    const fetchOrder = async () => {
      if (!clientToken) {
        setOrder(null);
        setLoading(false);
        setError("Please login before tracking this order.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${BACKEND_URL}/home-tests/orders/${orderId}`, {
          headers: {
            Authorization: `Bearer ${clientToken}`,
          },
        });

        if (!alive) return;

        const nextOrder = response.data?.order || null;
        setOrder(nextOrder);
        saveAccount(nextOrder?.client);
      } catch (err) {
        if (!alive) return;
        if (err.response?.status === 401) {
          localStorage.removeItem(CLIENT_TOKEN_KEY);
          setClientToken("");
          window.dispatchEvent(new Event("zaviramed-home-test-account"));
        }
        setError(err.response?.data?.message || "Could not load this order.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchOrder();

    return () => {
      alive = false;
    };
  }, [clientToken, orderId]);

  useEffect(() => {
    const syncToken = () => {
      setClientToken(getClientToken());
    };

    window.addEventListener("storage", syncToken);
    window.addEventListener("zaviramed-home-test-account", syncToken);

    return () => {
      window.removeEventListener("storage", syncToken);
      window.removeEventListener("zaviramed-home-test-account", syncToken);
    };
  }, []);

  const assistantMessage = useMemo(
    () =>
      "Hello Zaviramed, I need help tracking my home test order.",
    []
  );

  return (
    <main className="min-h-screen bg-[#f8faf9] text-slate-950">
      <ArticleSeo
        title="Track Home Test Order | Zaviramed"
        description="Track your Zaviramed home test order, dispatch status and rider details."
        canonical={`${SITE_URL}/orders/${orderId}`}
        type="website"
      />

      <Header />
      <GuestAccountMenu />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <Link
          to="/orders"
          className="mb-4 inline-flex text-sm font-black text-[#075E54] hover:underline"
        >
         <ArrowLeft className="relative  size-5" /> Back to orders
        </Link>

        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-sm font-bold text-slate-500 shadow-sm">
            <Loader2 className="mr-2 animate-spin" size={18} />
            Loading order
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm font-semibold text-red-700">
            {error}
          </div>
        ) : order ? (
          <div className="space-y-5">
            <section className="rounded-xl border border-slate-200 bg-white p-5  sm:p-8">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  {/* <p className="text-xs font-black uppercase tracking-[0.18em] text-[#25D366]">
                    Order tracking
                  </p> */}
                  <h1 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-[#075E54] sm:text-3xl">
                    {order.title || "Home test order"}
                  </h1>
                  <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                    Current status:{" "}
                    <span className="font-semibold text-slate-800">
                      {order.statusLabel}
                    </span>
                  </p>
                </div>

              </div>
            </section>

            <Stepper status={order.status} />

            <section className="grid gap-4 lg:grid-cols-2">
              <TrackingInfoCard
                icon={MapPin}
                title="Delivery address"
                artSrc="/tracking-address-art.png"
                artAlt=""
                artClassName="w-[170px] sm:w-[205px] lg:w-[230px]"
              >
                <p>
                  {order.deliveryAddress?.fullAddress || "No address added"}
                </p>
                {[order.deliveryAddress?.city, order.deliveryAddress?.state]
                  .filter(Boolean)
                  .join(", ") && (
                  <p>
                    {[order.deliveryAddress?.city, order.deliveryAddress?.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
                {order.deliveryAddress?.landmark && (
                  <p className="text-xs text-slate-500">
                    Landmark: {order.deliveryAddress.landmark}
                  </p>
                )}
              </TrackingInfoCard>

              <TrackingInfoCard
                icon={Truck}
                title="Rider details"
                artSrc="/tracking-rider-art.png"
                artAlt=""
                artClassName="w-[185px] sm:w-[220px] lg:w-[250px]"
              >
                {order.status === "dispatch" ||
                order.status === "delivered" ? (
                  <div className="space-y-1.5">
                    <p>Name: {order.rider?.name || "Not added yet"}</p>
                    <p>Phone: {order.rider?.phoneNumber || "Not added yet"}</p>
                    <p>Vehicle: {order.rider?.vehicle || "Not added yet"}</p>
                    <p>
                      Tracking code:{" "}
                      {order.rider?.trackingCode || "Not added yet"}
                    </p>
                  </div>
                ) : (
                  <p>
                    Rider details will show here when the package has been
                    dispatched.
                  </p>
                )}
              </TrackingInfoCard>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-black text-slate-950">
                Need help?
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="tel:+2348069804062"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-700 transition hover:bg-slate-50"
                >
                  <Phone size={15} />
                  Call assistant
                </a>
                <a
                  href={
                    order.whatsappUrl ||
                    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                      assistantMessage
                    )}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-xs font-black text-[#075E54] transition hover:bg-emerald-100"
                >
                  <WhatsApp size={15} />
                  WhatsApp assistant
                </a>
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <Footer />
    </main>
  );
}
